
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import {
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaUserCircle,
  FaSignOutAlt,
  FaUserTie,
} from "react-icons/fa";
import { signOut } from "aws-amplify/auth";
import Dashboard from "../component/approver/Dashboard";
import PendingTable from "../component/approver/PendingTable";
import ApprovedTable from "../component/approver/ApprovedTable";
import RejectedTable from "../component/approver/RejectedTable";
import translations from "../translation";
import "../App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import Form from "../component/user/Form";

const ApproverDashboard = () => {
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [language, setLanguage] = useState("en"); // Language state
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user"));

  // Update user info and set RTL/LTR on language change
  useEffect(() => {
    if (user) {
      setUserName(user.fullname || translations[language]?.unknownUser || "Unknown User");
      setUserRole(user.role || translations[language]?.defaultRole || "User");
    }

    // Update the direction attribute dynamically
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language, user]);

  // Set page title dynamically
  useEffect(() => {
    const pageTitleMap = {
      "/approver/dashboard": translate("dashboard"),
      "/approver/pending": translate("pendingAnnouncements"),
      "/approver/approved": translate("approvedAnnouncements"),
      "/approver/rejected": translate("rejectedAnnouncements"),
      "/approver/create-announcement": translate("createAnnouncement"),
    };
    document.title = pageTitleMap[location.pathname] || translate("approverDashboard");
  }, [location.pathname]);

  const translate = (key) => translations[language]?.[key] || key;

  const getLinkClasses = (path) => {
    const isActive = location.pathname === path;
    return `w-full flex items-center px-4 py-3 ${isActive ? "text-gray-700 bg-gray-100 font-semibold" : "text-gray-700"
      } hover:bg-gray-200`;
  };

  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.removeItem("user"); // Remove user info from localStorage
      window.location.href = "/login"; // Redirect to the login page
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  return (
    <>
      <div
        className={`d-flex ${language === "ar" ? "flex-row-reverse text-arabic" : "flex-row"}`}
      >
        {/* Sidebar */}
        <div
          className={`bg-white h-screen fixed ${language === "ar" ? "right-0 text-arabic" : "left-0"
            } border-r border-gray-200 flex flex-col justify-between w-64`}
        >
          {/* Header Section */}
          <div>
            {/* Logo */}
            <div className="flex items-center p-4 border-b border-gray-200">
              <img
                className="h-28 w-70 mt-2"
                src="/images/alnaghi.png"
                alt={translate("logoAlt") || "Logo"}
              />
            </div>

            {/* Navigation Links */}
            <nav className="mt-4">
              <Link
                to="/approver/dashboard"
                className={getLinkClasses("/approver/dashboard")}
                state={{ language }}
              >
                <FaChartLine className="w-5 h-5 mr-3 text-gray-600" />
                {translate("dashboard")}
              </Link>
              <Link
                to="/approver/create-announcement"
                className={getLinkClasses("/approver/create-announcement")}
                state={{ language }}
              >
                <FaUserTie className="w-5 h-5 mr-3" />
                {translate("createAnnouncement")}
              </Link>
              <Link
                to="/approver/pending"
                className={getLinkClasses("/approver/pending")}
                state={{ language }}
              >
                <FaClock className="w-5 h-5 mr-3 text-gray-600" />
                {translate("pendingAnnouncements")}
              </Link>
              <Link
                to="/approver/approved"
                className={getLinkClasses("/approver/approved")}
                state={{ language }}
              >
                <FaCheckCircle className="w-5 h-5 mr-3 text-gray-600" />
                {translate("approvedAnnouncements")}
              </Link>
              <Link
                to="/approver/rejected"
                className={getLinkClasses("/approver/rejected")}
                state={{ language }}
              >
                <FaTimesCircle className="w-5 h-5 mr-3 text-gray-600" />
                {translate("rejectedAnnouncements")}
              </Link>
            </nav>
          </div>

          {/* Footer Section */}
          <div className="p-2 border-t border-gray-200">
            {/* Language Selector */}
            <select
              className="w-full text-sm bg-gray-100 border border-gray-300 text-gray-700 py-2 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
              value={language}
              onChange={handleLanguageChange}
            >
              <option value="en">{translate("languageEnglish")}</option>
              <option value="ar">{translate("languageArabic")}</option>
            </select>

            {/* User Info */}
            <div className="flex items-center">
              <FaUserCircle className="w-10 h-10 text-gray-500 ml-4" />
              <div>
                <p className="text-lg font-medium text-gray-700 ml-2">{userName}</p>
                <p className="text-sm text-gray-500 ml-2">
                  {translate("approverRole")}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center rounded-sm bg-red-500 text-white px-4 py-3 hover:bg-red-400 hover:text-red-600 transition"
            >
              <FaSignOutAlt className="w-5 h-5 mr-3" />
              {translate("logout")}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <main
          className={`${language === "ar" ? "mr-64 text-arabic" : "ml-64"
            } max-w-8xl px-4 sm:px-6 lg:px-8 py-8 w-full ${language === "ar" ? "text-right text-arabic" : "text-left"
            }`}
        >
          <Routes>
            <Route path="dashboard" element={<Dashboard language={language} />} />
            <Route path="pending" element={<PendingTable language={language} />} />
            <Route path="approved" element={<ApprovedTable language={language} />} />
            <Route path="rejected" element={<RejectedTable language={language} />} />
            <Route path="create-announcement" element={<Form language={language} />} />
            {/* Default Route */}
            <Route path="/" element={<Navigate to="dashboard" />} />
          </Routes>
        </main>
      </div>
    </>
  );
};

export default ApproverDashboard;
